import React, { useEffect, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import { SettingInputField } from "./setting-driver-input-field"; // Dùng input mới

interface SettingField {
  name: string;
  label: string;
  placeholder: string;
  isRequired?: boolean;
  type?: "text" | "number";
  helpText?: string;
  min?: number;
}

interface SettingsFormCardProps<T> {
  title: string;
  fields: readonly SettingField[];
  getDetails: () => { fetch: () => void; response: any; loading: boolean };
  updateSettings: () => { fetch: (payload: T) => void; response: any; loading: boolean };
  dataKey?: string;
}

export const SettingsFormCard = <T,>({ title, fields, getDetails, updateSettings, dataKey = 'delivery_setting' }: SettingsFormCardProps<T>) => {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const { fetch: fetchDetails, response: detailsRes, loading: loadingDetails } = getDetails();
  const { fetch: fetchUpdate, response: updateRes, loading: loadingUpdate } = updateSettings();

  useEffect(() => {
    fetchDetails();
  }, []);

  useEffect(() => {
    if (detailsRes && isSuccess(detailsRes)) {
      const data = detailsRes.data?.[dataKey];
      if (!data) return;

      const newSettings = fields.reduce((acc, field) => {
        acc[field.name] = data[field.name] != null ? String(data[field.name]) : "";
        return acc;
      }, {} as Record<string, string>);
      setSettings(newSettings);
    }
  }, [detailsRes, dataKey]);

  useEffect(() => {
    if (updateRes) {
      if (isSuccess(updateRes)) {
        showToast({ message: "Cập nhật thành công!", type: "success" });
        fetchDetails();
      } else {
        showToast({ message: updateRes.message || "Có lỗi xảy ra", type: "error" });
      }
    }
  }, [updateRes]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
    // Xoá lỗi khi người dùng bắt đầu nhập
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = () => {
    const newErrors: Record<string, string | null> = {};
    let isValid = true;

    const requiredFields = fields.filter((field) => field.isRequired !== false);
    for (const field of requiredFields) {
      if (!settings[field.name] || String(settings[field.name]).trim() === "") {
        newErrors[field.name] = "Vui lòng điền đầy đủ thông tin bắt buộc.";
        isValid = false;
      }
    }

    for (const field of fields) {
      if (field.type === 'number' && field.min !== undefined) {
        const value = Number(settings[field.name]);
        if (settings[field.name] && value < field.min) {
          newErrors[field.name] = `${field.label} phải lớn hơn hoặc bằng ${field.min}.`;
          isValid = false;
        }
      }
    }

    setErrors(newErrors);
    if (!isValid) return;

    const payload: Record<string, string | number> = { ...settings };
    fields.forEach(field => {
      if (field.type === 'number' && payload[field.name]) {
        (payload as any)[field.name] = Number(payload[field.name]);
      }
    });

    fetchUpdate(payload as T);
  };

  return (
    <div className="w-full max-w-4xl p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold tracking-wide text-[#17A34A] flex items-center">{title}</h3>
      </div>

      {loadingDetails ? <div className="spinner">Đang tải...</div> : (
        <div className="space-y-4">
          {fields.map((field) => (
            <SettingInputField
              key={field.name}
              label={field.label}
              value={settings[field.name] ?? ""}
              placeholder={field.placeholder}
              isRequired={field.isRequired !== false}
              onChange={handleInputChange}
              name={field.name}
              type={field.type}
              helpText={field.helpText}
              error={errors[field.name]}
            />
          ))}
        </div>
      )}

      <div className="flex justify-end pt-6">
        {loadingUpdate || loadingDetails ? (
          <div className="spinner">Đang xử lý...</div>
        ) : (
          <button 
            onClick={handleSubmit} 
            className="px-6 py-2 text-sm font-medium text-white bg-green-500 border border-transparent rounded-md shadow-sm hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-150"
          >
            CẬP NHẬT
          </button>
        )}
      </div>
    </div>
  );
};